import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { get } from 'lodash';
import memoize from 'memoize-one';
import { BlockTabs, Button, ButtonGroup, Panel, TYPES } from 'sarsaparilla';
import { STATE_READY, STATE_UNLOADED } from '../../../../ui-lottery/src/utils/async_utils';
import { fetchAsset, resetAsset } from '../../actions/asset';
import { removeRefs, clearChanges, saveChanges } from '../../actions/changes';
import { fetchOrganizations } from '../../actions/organization';
import { CurrentUserProps } from '../../props/currentUser';
import { OrganizationProps } from '../../props/organization';
import { getCurrentUser } from '../../reducers/currentUser';
import LoadingWrapper from '../../../../ui-lottery/src/components/LoadingWrapper';
import AssetTab from './AssetTab';
import ActivitiesTab from './ActivitiesTab';
import AddressesTab from '../../components/StewardAssetEdit/AddressesTab';
import LinkTab from '../../components/StewardAssetEdit/LinkTab';
import SiteMapTab from '../../components/StewardAssetEdit/SiteMapTab';
import EventsTab from '../../components/StewardAssetEdit/EventsTab';
import ImagesTab from '../../components/StewardAssetEdit/ImagesTab';
import { getState } from '../../reducers';
import { getAsset } from '../../reducers/asset';
import { getChangeCount, getErrorCount } from '../../reducers/changes';
import { getOrganization } from '../../reducers/organizations';
import { flattenOrg, orgsToOptions } from '../../utils/organization';
import { assetTypeDetails } from '../../utils/asset';
import { saveClearErrors } from '../../actions/saveStatus';
import SaveStatus from './SaveStatus';

import styles from './StewardAssetEditPage.scss';

const propTypes = {
  match: TYPES.MATCH,
  history: TYPES.HISTORY,
  states: PropTypes.objectOf(PropTypes.number),
  currentUser: CurrentUserProps,
  asset: PropTypes.shape({}),
  organization: OrganizationProps,
  changes: PropTypes.arrayOf(PropTypes.object),
  fetchAsset: PropTypes.func.isRequired,
  resetAsset: PropTypes.func.isRequired,
  clearChanges: PropTypes.func.isRequired,
  saveChanges: PropTypes.func.isRequired,
  fetchOrganizations: PropTypes.func.isRequired,
  removeRefs: PropTypes.func.isRequired,
  saveClearErrors: PropTypes.func.isRequired,
  changeCount: PropTypes.number.isRequired,
  errorCount: PropTypes.number.isRequired
};

export class StewardAssetEditPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      tabClasses: {}
    };

    this.doCancel = this.doCancel.bind(this);
    this.doSave = this.doSave.bind(this);
  }

  componentDidMount() {
    if (this.props.states.organizations === STATE_UNLOADED) {
      this.props.fetchOrganizations();
    }

    if (this.props.match.params.assetId) {
      this.fetchAsset();
    }
  }

  componentWillUnmount() {
    this.props.removeRefs();
    this.props.saveClearErrors();
  }

  getOrgOptions = memoize((organizationsState, currentUserState, organization) => {
    if (organizationsState === STATE_READY && currentUserState === STATE_READY) {
      if (organization) {
        return orgsToOptions(flattenOrg(organization));
      }
    }

    return [];
  });

  getTabClasses = memoize((changes) => {
    const tabClasses = {
      asset: '',
      activities: '',
      addresses: '',
      links: '',
      images: '',
      events: ''
    };

    changes.forEach((change) => {
      if (change.validation && Object.keys(change.validation).length > 0) {
        switch (change.type) {
          case 'recareas':
          case 'facilities':
            tabClasses.asset = styles['tab-error'];
            break;
          case 'facilityactivities':
          case 'recareaactivities':
            tabClasses.activities = styles['tab-error'];
            break;
          case 'facilityaddresses':
          case 'recareaaddresses':
            tabClasses.addresses = styles['tab-error'];
            break;
          case 'facilitylinks':
          case 'recarealinks':
            tabClasses.links = styles['tab-error'];
            break;
          case 'facilitymedia':
          case 'recareamedia':
            tabClasses.images = styles['tab-error'];
            break;
          case 'facilityevents':
          case 'recareaevents':
            tabClasses.events = styles['tab-error'];
            break;
          default:
          // noop
        }
      }
    });

    return tabClasses;
  });

  fetchAsset() {
    if (this.assetNeedsUpdate()) {
      this.props.fetchAsset(this.props.match.params.assetType, this.props.match.params.assetId, { full: true });
    }
  }

  assetNeedsUpdate() {
    return this.props.match.params.assetId && get(this.props, 'asset.id') !== this.props.match.params.assetId;
  }

  doCancel() {
    const { assetType } = this.props.match.params;
    this.props.history.push(`/data-steward/${assetType}/search`);
  }

  doSave() {
    const { changes, match: { params: { assetType, assetId } } } = this.props;

    this.props.saveChanges(changes, assetId)
      .then(() => {
        this.props.resetAsset();
        this.props.history.push(`/data-steward/${assetType}/${assetId}/display`);
      })
      .catch(console.log);
  }

  render() {
    const {
      match: { params: { assetId, assetType } },
      states: {organizations: organizationsState, currentUser: currentUserState},
      organization,
      asset,
      changes,
      changeCount,
      errorCount
    } = this.props;
    const tabClasses = this.getTabClasses(changes);
    const orgOptions = this.getOrgOptions(organizationsState, currentUserState, organization);
    const details = assetTypeDetails(assetType);
    const loading = this.props.states.asset !== STATE_READY || assetId !== asset.id;
    const reservable = get(asset, 'reservable', false);

    return (
      <div className={styles['steward-asset-edit']}>
        {!reservable ? null :
          <div className={`${styles['notice-section']}`}>
            <p><strong>Warning:</strong></p>
            <p>This {details.name} is not editable.</p>
          </div>
        }
        <SaveStatus />
        <BlockTabs indentTabs>
          <Panel label={<span className={tabClasses.asset}>{details.name}</span>} className={styles['asset-tab']}>
            <LoadingWrapper loading={loading} size="large">
              <AssetTab
                assetDetails={details}
                value={asset}
                orgOptions={orgOptions} />
            </LoadingWrapper>
          </Panel>
          <Panel label={<span className={tabClasses.activities}>Activities</span>} className={styles['activities-tab']}>
            <LoadingWrapper loading={loading} size="large">
              <ActivitiesTab
                activityType={details.activities}
                value={asset} />
            </LoadingWrapper>
          </Panel>
          <Panel label={<span className={tabClasses.addresses}>Addresses</span>} className={styles['addresses-tab']}>
            <LoadingWrapper loading={loading} size="large">
              <AddressesTab
                addressType={details.addresses}
                value={asset} />
            </LoadingWrapper>
          </Panel>
          <Panel label={<span className={tabClasses.links}>Links</span>} className={styles['links-tab']}>
            <LoadingWrapper loading={loading} size="large">
              <LinkTab
                linkType={details.links}
                value={asset} />
            </LoadingWrapper>
          </Panel>
          <Panel label={<span className={tabClasses.images}>Images</span>} className={styles['images-tab']}>
            <LoadingWrapper loading={loading} size="large">
              <ImagesTab
                mediaType={details.media}
                value={asset} />
            </LoadingWrapper>
          </Panel>
          <Panel label={<span className={tabClasses.events}>Events</span>} className={styles['events-tab']}>
            <LoadingWrapper loading={loading} size="large">
              <EventsTab
                eventType={details.event}
                assetType={assetType}
                eventsToLoad={asset.eventsToLoad}
                asset={asset} />
            </LoadingWrapper>
          </Panel>
          <Panel label="Site Maps" className={styles['siteMaps-tab']}>
            <LoadingWrapper loading={loading} size="large">
              <SiteMapTab />
            </LoadingWrapper>
          </Panel>
        </BlockTabs>
        <div className={styles['form-footer']}>
          <div className={styles['footer-status']}>
            STATUS: {changeCount} Changes, {errorCount} Errors
          </div>
          <ButtonGroup>
            <Button appearance="tertiary" onClick={this.doCancel}>Cancel</Button>
            {reservable ? null :
              <Button onClick={this.doSave} isLoading={loading}>Save Changes</Button>
            }
          </ButtonGroup>
        </div>
      </div>
    );
  }
}

StewardAssetEditPage.propTypes = propTypes;

const mapStateToProps = (state) => ({
  currentUser: getCurrentUser(state),
  asset: getAsset(state),
  organization: getOrganization(state, getCurrentUser(state).apiKey.orgId),
  states: getState(state, 'organizations', 'currentUser', 'asset'),
  changes: state.portal.changes,
  changeCount: getChangeCount(state),
  errorCount: getErrorCount(state)
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchOrganizations,
  fetchAsset,
  resetAsset,
  saveChanges,
  clearChanges,
  removeRefs,
  saveClearErrors
}, dispatch);

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(StewardAssetEditPage));
